import {
	Button,
	Checkbox,
	DatePicker,
	Modal,
	Skeleton,
	Space,
	Table,
	Empty,
} from 'antd';
import dayjs from 'dayjs';
import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	CostAllocation,
	EditActionSvg,
	EmployeeCost,
	QuickbooksEmployee,
	// JournalEntries,
	TickSmall,
	TimeLogs,
} from 'utils/svgs';
import { checkPermission } from 'utils/utils';
import SearchAndFilter from '../SearchAndFilter/index';
import './index.scss';
import { DynamicTableProps } from './types';
import { DeleteOutlined } from '@ant-design/icons';
import { deleteApi } from 'redux/apis';
import { AppDispatch } from 'redux/store';
import { payPeriodAction, payPeriodOptionsAction } from 'redux/action/payPeriodAction';

// import moment from 'moment';

import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getJournalListingAction } from 'redux/action/journalAction';
import { getEmployeeCostColumnAction } from 'redux/action/employeeCostColumnSlice';
import { toastText } from 'utils/utils';

dayjs.extend(utc);
dayjs.extend(timezone);

const TIMEZONE = process.env.REACT_APP_TIMEZONE || 'America/New_York';

const DynamicTable: FC<DynamicTableProps> = (props) => {
	// Inits
	const { Column } = Table;
	const {
		payPeriodData,
		filterValue,
		performSortHandler,
		savePayPeriodHandler,
		editClickHandler,
		payPeriodDates,
		editPayPeriodHandler,
		performFilterHandler,
		payPeriodYear,
		tableLoading,
		changeEmployeeCost,
		totalRecords,
		paginationChangeHandler,
		currentPage,
		setCurrentPage,
		// focusChangeHandler,
	} = props;
	const dispatch = useDispatch<AppDispatch>();
	const { RangePicker } = DatePicker;
	const navigate = useNavigate();
	const qboMode = process.env.REACT_APP_QBO_MODE
		? process.env.REACT_APP_QBO_MODE
		: 'sandbox';
	// For handle the table change click
	const tableChangeHandler = (_: any, __: any, data: any) => {
		performSortHandler && performSortHandler(data.order);
	};
	const [searchParams, setSearchParams] = useSearchParams();
	function isDateDisabled(date: any) {
		// Convert the date to a string in 'YYYY-MM-DD' format
		const dateString = date.format('YYYY-MM-DD');

		// Check if the dateString is included in the disabledDates array
		return payPeriodDates.includes(dateString);
	}

	const selectedCompanyPermission = useSelector(
		(state: any) => state?.companies?.selectedCompanyDetails?.role?.permissions
	);

	const isEditPayPeriodPermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Pay Period',
		permission: ['edit'],
	});

	const employeeCostChickHandler = (data: any) => {
		navigate(`/employee-costs?payPeriodId=${data.id}`);
		changeEmployeeCost && changeEmployeeCost('1');
	};
	const [qboLoading, setQboLoading] = useState(false);
	const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [selectedPayPeriod, setSelectedPayPeriod] = useState<any>(null);
	const [publishedDeleteTarget, setPublishedDeleteTarget] = useState<any>(null);
	const isModalMountedRef = useRef(false);
	const [isDeleteConfirmed, setIsDeleteConfirmed] = useState(false);
	const handleDeleteClick = (payPeriod: any) => {
		setSelectedPayPeriod(payPeriod);
		setIsDeleteModalVisible(true);
	};

	const confirmDelete = async () => {
		setDeleteLoading(true);
		try {
			const response = await deleteApi(`/pay-periods/${selectedPayPeriod.id}`);

			// Check if response status is 200 (successful deletion)
			if (response.status === 200) {
				toastText('Pay period deleted successfully.', 'success');
				// Remove the payPeriodId query parameter from the URL
				setIsDeleteModalVisible(false);
				setSelectedPayPeriod(null);
				const query: any = {
					page: 1,
					limit: 10,
				};
				if (filterValue) {
					query.year = filterValue;
				}
				dispatch(payPeriodAction(query));
				dispatch(payPeriodOptionsAction());
				setCurrentPage(1);
				localStorage.removeItem('payPeriod');
				dispatch(getEmployeeCostColumnAction(query));
				searchParams.delete('payPeriodId');
				setSearchParams(searchParams);
			} else {
				// If status is not 200, show failure message
				toastText('Failed to delete pay period', 'error');
			}
		} catch (error) {
			console.error('Delete error:', error);
			toastText('Failed to delete pay period', 'error');
		} finally {
			setDeleteLoading(false); // Ensure loading state is reset after operation
		}
	};
	const confirmDeletePublished = async (data: any) => {
		setDeleteLoading(true);
		try {
			const response = await deleteApi(`/pay-periods/${data.id}`);

			if (response.status === 200) {
				toastText('Published pay period deleted successfully', 'success');
				setPublishedDeleteTarget(null);
				setIsDeleteConfirmed(false);

				// Refresh pay periods list
				const query: any = {
					page: 1,
					limit: 10,
				};
				if (filterValue) {
					query.year = filterValue;
				}
				dispatch(payPeriodAction(query));
				dispatch(payPeriodOptionsAction());
				dispatch(getEmployeeCostColumnAction(query));
				setCurrentPage(1);
				localStorage.removeItem('payPeriod');
				searchParams.delete('payPeriodId');
				setSearchParams(searchParams);
			} else {
				toastText('Failed to delete published pay period', 'error');
			}
		} catch (error) {
			console.error('Error deleting published pay period:', error);
			toastText('Failed to delete published pay period', 'error');
		} finally {
			setDeleteLoading(false);
		}
	};

	useEffect(() => {
		if (publishedDeleteTarget?.id) {
			isModalMountedRef.current = true;
			setQboLoading(true);

			const fetchQboJournalData = async () => {
				try {
					const res = await dispatch(
						getJournalListingAction({ payPeriodId: publishedDeleteTarget.id })
					);

					if (
						isModalMountedRef.current &&
						getJournalListingAction.fulfilled.match(res)
					) {
						const journalEntry = res.payload?.data?.journals?.[0];
						if (journalEntry?.qboJournalTrnId) {
							setPublishedDeleteTarget((prev: any) => ({
								...prev,
								qboLink: journalEntry.qboJournalTrnId,
								qboJournalNo: journalEntry.qboJournalNo,
							}));
						}
					}
				} finally {
					if (isModalMountedRef.current) setQboLoading(false);
				}
			};

			fetchQboJournalData();
		}
		return () => {
			isModalMountedRef.current = false;
		};
	}, [publishedDeleteTarget?.id]);

	const hasActiveFilters = Boolean(filterValue);

	const CustomEmptyState = () => (
		<Empty
			image={Empty.PRESENTED_IMAGE_SIMPLE}
			description={
				tableLoading
					? null
					: hasActiveFilters
						? "No Pay Period records match the selected filter criteria"
						: "No Pay Period records are currently available"
			}
			style={{
				padding: '40px 20px',
				margin: '20px 0'
			}}
		/>
	);

	// JSX
	return (
		<div className={'dynamic-table'}>
			<SearchAndFilter
				searchValue={''}
				filterValue={filterValue}
				performFilterHandler={performFilterHandler}
				payPeriodYear={payPeriodYear}
			/>
			<Table
				dataSource={payPeriodData}
				scroll={{ y: '50vh' }}
				className="table-global"
				onChange={tableChangeHandler}
				rowKey={(record: any) => {
					return record.id;
				}}
				loading={tableLoading}
				locale={{
					emptyText: <CustomEmptyState />
				}}
				pagination={
					(totalRecords || 0) <= 10
						? false
						: {
							current: Math.min(
								currentPage || 1,
								Math.max(1, Math.ceil((totalRecords || 0) / 10))
							),
							pageSize: 10,
							style: { fontSize: '12px' },
							total: totalRecords,
							onChange: paginationChangeHandler,
							showSizeChanger: false,
							showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
							position: ['bottomRight'],
							hideOnSinglePage: true,
							responsive: true,
						}
				}
			>
				<Column
					// title="Sr. No"
					title={() => (
						<p
							style={{
								textAlign: 'center',
								fontSize: '12px',
							}}
						>
							Sr. No
						</p>
					)}
					dataIndex="index"
					key="index"
					className="bg-white"
					width={'10%'}
					render={(value: any, data: any, index: number) => {
						return (
							<div className="pay-period-column">
								<p style={{ textAlign: 'center' }}>
									{(currentPage - 1) * 10 + index + 1}
								</p>
							</div>
						);
					}}
				/>
				{/* <Column
					title={() => (
						<p
							style={{
								textAlign: 'center',
							}}
						>
							Pay Periods
						</p>
					)}
					dataIndex="phone"
					key="phone"
					className={`bg-white`}
					width={'230px'}
					render={(value: any, data: any) => {
						const startDate: any = data.startDate
							? new Date(data.startDate)
							: null;
						const endDate: any = data.endDate ? new Date(data.endDate) : null;
						const selectedRange: any = [
							startDate ? dayjs(startDate) : null,
							startDate ? dayjs(endDate) : null,
						];
						return (
							<div className="pay-period-column">
								{!data.isEditing ? (
									<div className="date">
										<p>
											<span>{startDate.getMonth() + 1}</span>
											<span>/</span>
											<span>{startDate.getDate()}</span>
											<span>/</span>
											<span>{startDate.getFullYear()}</span>
										</p>
										<p>-</p>
										<p>
											<span>{endDate.getMonth() + 1}</span>
											<span>/</span>
											<span>{endDate.getDate()}</span>
											<span>/</span>
											<span>{endDate.getFullYear()}</span>
										</p>
									</div>
								) : (
									<RangePicker
										className={`range-picker ${
											data.isError && 'err-range-picker'
										}`}
										size="large"
										disabledDate={(date) => isDateDisabled(date)}
										style={{ width: 260 }}
										// onFocus={() => {
										// 	focusChangeHandler(data);
										// }}
										status={data.isError}
										format="MM/DD/YYYY"
										value={selectedRange}
										onChange={(rangeData: any) => {
											editPayPeriodHandler(
												data,
												rangeData?.[0]?.['$d'] || null,
												rangeData?.[1]?.['$d'] || null
											);
										}}
									/>
								)}
							</div>
						);
					}}
				/> */}
				<Column
					title={() => (
						<p
							style={{
								textAlign: 'center',
								fontSize: '12px',
							}}
						>
							Pay Periods
						</p>
					)}
					dataIndex="phone"
					key="phone"
					className={`bg-white`}
					width={'230px'}
					render={(value: any, data: any) => {
						const startDate = data.startDate
							? dayjs.tz(data.startDate, TIMEZONE)
							: null;
						const endDate = data.endDate
							? dayjs.tz(data.endDate, TIMEZONE)
							: null;

						const selectedRange: any = [startDate, endDate];
						return (
							<div className="pay-period-column">
								{!data.isEditing ? (
									<div className="date">
										{startDate && endDate ? (
											<>
												<p>
													<span>{startDate.month() + 1}</span>
													<span>/</span>
													<span>{startDate.date()}</span>
													<span>/</span>
													<span>{startDate.year()}</span>
												</p>
												<p>-</p>
												<p>
													<span>{endDate.month() + 1}</span>
													<span>/</span>
													<span>{endDate.date()}</span>
													<span>/</span>
													<span>{endDate.year()}</span>
												</p>
											</>
										) : (
											<p>Invalid Dates</p>
										)}
									</div>
								) : (
									<RangePicker
										className={`range-picker ${data.isError ? 'err-range-picker' : ''
											}`}
										size="large"
										disabledDate={(date) => isDateDisabled(date)}
										style={{ width: 260, fontSize: '12px' }}
										status={data.isError}
										format="MM/DD/YYYY"
										value={selectedRange}
										onChange={(rangeData: any) => {
											const start: string = rangeData?.[0]
												? dayjs(rangeData[0]).tz(TIMEZONE, true).toISOString()
												: '';

											const end: string = rangeData?.[1]
												? dayjs(rangeData[1]).tz(TIMEZONE, true).toISOString()
												: '';

											editPayPeriodHandler(data, start, end);
										}}
									/>
								)}
							</div>
						);
					}}
				/>

				{/* <Column
					title={() => (
						<p
							style={{
								textAlign: 'center',
							}}
						>
							QuickBooks Closing Date
						</p>
					)}
					dataIndex="closingDate"
					key="closingDate"
					className={`bg-white`}
					width={'230px'}
					render={(value: any) => {
						return (
							<div className="pay-period-column">
								{value ? moment(value).format('MM/DD/YYYY') : ''}
							</div>
						);
					}}
				/> */}
				<Column
					title={() => (
						<p
							style={{
								textAlign: 'center',
								fontSize: '12px',
							}}
						>
							Employee Costs
						</p>
					)}
					dataIndex="phone"
					key="phone"
					className={`bg-white`}
					render={(value: any, data: any) => {
						return (
							<div className="pay-period-column">
								<div className="section-time">
									<p
										className="employee-cost-pay-period"
										onClick={() => employeeCostChickHandler(data)}
									>
										<EmployeeCost />
									</p>
								</div>
							</div>
						);
					}}
				/>

				<Column
					title={() => (
						<p
							style={{
								textAlign: 'center',
								fontSize: '12px',
							}}
						>
							Time Log
						</p>
					)}
					dataIndex="phone"
					key="phone"
					className={`bg-white`}
					render={(value: any, data: any) => {
						return (
							<div className="pay-period-column">
								<div className="section-time">
									<p
										className="employee-cost-pay-period"
										onClick={() =>
											navigate(`/time-activity?payPeriod=${data.id}`)
										}
									>
										<TimeLogs />
									</p>
								</div>
							</div>
						);
					}}
				/>
				<Column
					title={() => (
						<p
							style={{
								textAlign: 'center',
								fontSize: '12px',
							}}
						>
							CostAllocations
						</p>
					)}
					dataIndex="phone"
					key="phone"
					className={`bg-white`}
					render={(value: any, data: any) => {
						return (
							<div className="pay-period-column">
								<div className="section-journal">
									<p
										className="employee-cost-pay-period"
										onClick={() =>
											navigate(`/cost-allocations?payPeriod=${data.id}`)
										}
									>
										<CostAllocation />
									</p>
								</div>
							</div>
						);
					}}
				/>
				<Column
					title={() => (
						<p
							style={{
								textAlign: 'center',
								fontSize: '12px',
							}}
						>
							Status
						</p>
					)}
					dataIndex="isJournalPublished"
					key="isJournalPublished"
					className={`bg-white`}
					render={(value: any) => {
						return (
							<div className="pay-period-column">
								<div className="section-time">
									<div className="section-journal">
										<p className="payPeriod-status">
											{value ? (
												<span className="payPeriod-status_green">
													Published
												</span>
											) : (
												<span className="payPeriod-status_yellow">Current</span>
											)}
										</p>
									</div>
								</div>
							</div>
						);
					}}
				/>
				{isEditPayPeriodPermission && (
					<Column
						// title="Action"
						title={() => (
							<p
								style={{
									textAlign: 'center',
									fontSize: '12px',
								}}
							>
								Action
							</p>
						)}
						dataIndex="action"
						key="action"
						className="bg-white"
						width={'10%'}
						align="center"
						render={(values, data: any) => {
							const isPublished = data.isJournalPublished;

							return (
								<Space size={20}>
									{!data.isEditing ? (
										<>
											<div
												className="cursor-pointer flex align-center justify-center"
												onClick={() => editClickHandler(data)}
											>
												<EditActionSvg />
											</div>
											<div className="flex align-center justify-center">
												<DeleteOutlined
													// onClick={() =>
													// 	!isPublished && handleDeleteClick(data)
													// }
													onClick={() =>
														isPublished
															? setPublishedDeleteTarget(data)
															: handleDeleteClick(data)
													}
													style={{
														// fontSize: '16px',
														// color: isPublished ? '#ccc' : 'red',
														// cursor: isPublished ? 'not-allowed' : 'pointer',
														// opacity: isPublished ? 0.5 : 1,
														fontSize: '16px',
														color: 'red',
														cursor: 'pointer',
														opacity: 1,
													}}
												/>
											</div>
										</>
									) : (
										<div
											className="cursor-pointer flex align-center justify-center action-svg"
											onClick={() => savePayPeriodHandler(data)}
										>
											<TickSmall />
										</div>
									)}
								</Space>
							);
						}}
					/>
				)}
			</Table>

			{isDeleteModalVisible && (
				<Modal
					// title="Confirm Delete"
					title={
						<p style={{ fontSize: '16px', fontWeight: 500 }}>Confirm Delete</p>
					}
					open={isDeleteModalVisible}
					onOk={confirmDelete}
					maskClosable={false}
					onCancel={() => setIsDeleteModalVisible(false)}
					okText="Yes, Delete"
					className="delete-pay-period-modal"
					okButtonProps={{
						danger: true,
						loading: deleteLoading,
						style: {
							backgroundColor: '#344735',
							borderColor: '#344735',
							color: '#fff',
							fontSize: '12px',
							borderRadius: '12px',
						},
					}}
					cancelButtonProps={{
						style: {
							backgroundColor: '#fff',
							borderColor: '#1e2b1f',
							color: '#172014',
							fontSize: '12px',
							borderRadius: '12px',
						},
					}}
					cancelText="Cancel"
				>
					<p style={{ fontSize: '12px' }}>
						You are about to <strong>permanently delete</strong> this pay period
						and all its associated data. This includes:
					</p>
					<ul style={{ marginLeft: 20, fontSize: '12px' }}>
						<li>Employee costs</li>
						<li>Settings and configurations</li>
						<li>TimeActivity entries</li>
						<li>Fixed employee rates</li>
					</ul>
					<p>
						<strong style={{ color: '#ff4d4f', fontSize: '12px' }}>
							Pay Period:{' '}
							{dayjs(selectedPayPeriod?.startDate)
								.tz(TIMEZONE)
								.format('MM-DD-YYYY')}{' '}
							to{' '}
							{dayjs(selectedPayPeriod?.endDate)
								.tz(TIMEZONE)
								.format('MM-DD-YYYY')}
						</strong>
					</p>
					<p style={{ marginTop: 10, fontWeight: 500, fontSize: '12px' }}>
						This action cannot be undone. Are you sure you want to continue?
					</p>
				</Modal>
			)}
			{publishedDeleteTarget && (
				<Modal
					// title="Delete Published Pay Period"
					title={
						<p style={{ fontSize: '16px', fontWeight: 500 }}>
							Delete Published Pay Period
						</p>
					}
					open={!!publishedDeleteTarget}
					onCancel={() => {
						setPublishedDeleteTarget(null);
						setIsDeleteConfirmed(false);
					}}
					maskClosable={false}
					footer={null}
					className="delete-pay-period-modal"
				>
					<p style={{ fontSize: '12px' }}>
						This pay period has a journal entry that has been synced to{' '}
						<strong>QuickBooks Online</strong>.
					</p>
					<p style={{ marginTop: 8, fontSize: '12px' }}>
						Deleting this pay period will{' '}
						<strong>not automatically remove</strong> the journal entry from
						QBO.
					</p>
					<p style={{ marginTop: 8, fontSize: '12px' }}>
						Please ensure the journal entry is manually deleted in QuickBooks if
						needed.
					</p>
					{/* {publishedDeleteTarget?.qboLink ? (
						<a
							href={`https://app${
								qboMode !== 'prod' ? '.sandbox' : ''
							}.qbo.intuit.com/app/journal?txnId=${
								publishedDeleteTarget.qboLink
							}`}
							target="_blank"
							rel="noopener noreferrer"
							style={{
								color: '#1677ff',
								display: 'inline-flex',
								alignItems: 'center',
								gap: 6,
								fontWeight: 500,
							}}
						>
							<span>
								View Journal Entry in QuickBooks
								{publishedDeleteTarget?.qboJournalNo &&
									` (#${publishedDeleteTarget.qboJournalNo})`}
							</span>
							<QuickbooksEmployee />
						</a>
					) : (
						<span
							style={{
								color: 'rgba(0, 0, 0, 0.3)',
								display: 'inline-flex',
								alignItems: 'center',
								gap: 6,
								cursor: 'not-allowed',
							}}
						>
						</span>
					)} */}

					<div style={{ marginTop: 12 }}>
						{qboLoading ? (
							<Skeleton.Input active size="small" style={{ width: 280 }} />
						) : publishedDeleteTarget?.qboLink ? (
							<a
								href={`https://app${qboMode !== 'prod' ? '.sandbox' : ''
									}.qbo.intuit.com/app/journal?txnId=${publishedDeleteTarget.qboLink
									}`}
								target="_blank"
								rel="noopener noreferrer"
								style={{
									color: '#1677ff',
									display: 'inline-flex',
									alignItems: 'center',
									gap: 6,
									fontWeight: 500,
									fontSize: '12px',
								}}
							>
								<span>
									View Journal Entry in QuickBooks
									{publishedDeleteTarget?.qboJournalNo &&
										` (#${publishedDeleteTarget.qboJournalNo})`}
								</span>
								<QuickbooksEmployee />
							</a>
						) : (
							<span
								style={{
									color: 'rgba(0, 0, 0, 0.3)',
									display: 'inline-flex',
									alignItems: 'center',
									gap: 6,
									fontSize: '12px',
								}}
							>
								Journal entry not found
							</span>
						)}
					</div>

					<p style={{ marginTop: 16, fontSize: '12px' }}>
						You are about to <strong>permanently delete</strong> this pay period
						and all its associated data. This includes:
					</p>
					<ul style={{ marginLeft: 20, fontSize: '12px' }}>
						<li>Employee costs</li>
						<li>Settings and configurations</li>
						<li>TimeActivity entries</li>
						<li>Fixed employee rates</li>
					</ul>

					<p style={{ marginTop: 12, fontSize: '12px' }}>
						<strong style={{ color: '#ff4d4f' }}>
							Pay Period:{' '}
							{dayjs(publishedDeleteTarget?.startDate)
								.tz(TIMEZONE)
								.format('MM-DD-YYYY')}{' '}
							to{' '}
							{dayjs(publishedDeleteTarget?.endDate)
								.tz(TIMEZONE)
								.format('MM-DD-YYYY')}
						</strong>
					</p>

					<p style={{ marginTop: 10, fontWeight: 500, fontSize: '12px' }}>
						This action cannot be undone. Are you sure you want to continue?
					</p>

					<div
						style={{ display: 'flex', alignItems: 'flex-start', marginTop: 12 }}
					>
						<Checkbox
							checked={isDeleteConfirmed}
							onChange={(e) => setIsDeleteConfirmed(e.target.checked)}
						/>
						<p style={{ marginLeft: 8, fontSize: '12px' }}>
							I understand this will not remove the journal entry from QBO and
							must be done manually.
						</p>
					</div>

					<Space
						style={{ width: '100%', justifyContent: 'flex-end', marginTop: 24 }}
					>
						<Button
							onClick={() => {
								setPublishedDeleteTarget(null);
								setIsDeleteConfirmed(false);
							}}
							style={{
								backgroundColor: '#fff',
								borderColor: '#1e2b1f',
								color: '#172014',
								fontSize: '12px',
								borderRadius: '12px',
							}}
						>
							Cancel
						</Button>
						<Button
							type="primary"
							danger
							disabled={
								!isDeleteConfirmed ||
								qboLoading ||
								!publishedDeleteTarget?.qboLink
							}
							loading={deleteLoading}
							onClick={() => confirmDeletePublished(publishedDeleteTarget)}
							style={{
								backgroundColor: '#344735',
								borderColor: '#344735',
								fontSize: '12px',
								borderRadius: '12px',

								color:
									isDeleteConfirmed &&
										!qboLoading &&
										publishedDeleteTarget?.qboLink
										? 'white'
										: '#ececec',
								cursor:
									isDeleteConfirmed &&
										!qboLoading &&
										publishedDeleteTarget?.qboLink
										? 'pointer'
										: 'not-allowed',
							}}
						>
							Yes, Delete
						</Button>
					</Space>
				</Modal>
			)}
		</div>
	);
};

export default DynamicTable;
